/**
 * Store d'authentification
 * Gère la session utilisateur avec Supabase Auth
 */
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useLocalStorage } from '@vueuse/core'
import { supabase } from '@/services/supabase'
import { apiFetch } from '@/services/api'

const ADMIN_ROLES = ['admin', 'super_admin']

export const useAuthStore = defineStore('auth', () => {
  // ============ STATE ============
  
  const user = ref(null)
  const session = ref(null)
  const isLoading = ref(false)
  const error = ref(null)
  
  // Persiste l'état de connexion
  const isAuthenticated = useLocalStorage('pdm-auth-authenticated', false)
  
  // ============ GETTERS ============
  
  const isAdmin = computed(() => {
    if (!user.value) return false
    // Le compte Supabase expose le rôle via `type` (admin|agent) ; on garde
    // `role`/`role_name` en compat avec d'éventuels autres providers.
    const candidate = (
      user.value.type ||
      user.value.role ||
      user.value.role_name ||
      ''
    ).toString().toLowerCase()
    return ADMIN_ROLES.includes(candidate)
  })

  const userType = computed(() => user.value?.type || user.value?.role)
  const isNotary = computed(() => String(userType.value || '').toLowerCase() === 'notary')
  
  const userInitials = computed(() => {
    if (!user.value) return ''
    const first = user.value.firstname?.[0] || ''
    const last = user.value.lastname?.[0] || ''
    return (first + last).toUpperCase()
  })
  
  const userDisplayName = computed(() => {
    if (!user.value) return ''
    return `${user.value.firstname || ''} ${user.value.lastname || ''}`.trim()
  })
  
  // ============ ACTIONS ============
  
  /**
   * Vérifie le statut d'authentification actuel
   */
  const checkAuthStatus = async () => {
    try {
      isLoading.value = true
      error.value = null
      
      // Appel à l'API pour récupérer le profil (vérifie aussi la session via cookies)
      const response = await apiFetch('/api/private/auth/me')
      
      if (response.ok) {
        const data = await response.json()
        user.value = data.account
        session.value = { user: data.user }
        isAuthenticated.value = true
        return true
      } else {
        clearAuth()
        return false
      }
    } catch (err) {
      console.error('Erreur vérification auth:', err)
      error.value = err.message
      clearAuth()
      return false
    } finally {
      isLoading.value = false
    }
  }
  
  /**
   * Récupère le profil utilisateur depuis l'API
   */
  const fetchUserProfile = async () => {
    try {
      const response = await apiFetch('/api/private/auth/me')
      
      if (!response.ok) {
        throw new Error('Impossible de récupérer le profil')
      }
      
      const data = await response.json()
      user.value = data.account
      session.value = { user: data.user }
      isAuthenticated.value = true
      return data.account
    } catch (err) {
      console.error('Erreur récupération profil:', err)
      throw err
    }
  }
  
  /**
   * Connexion avec email/password
   */
  const login = async (email, password) => {
    try {
      isLoading.value = true
      error.value = null
      
      const response = await apiFetch('/api/public/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      })
      
      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Erreur de connexion')
      }
      
      // Récupérer le profil après connexion réussie
      await fetchUserProfile()
      
      return true
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }
  
  /**
   * Connexion avec Google OAuth.
   *
   * Stratégie alignée sur l'existant (`/src/stores/auth.js`) :
   *   - On construit l'URL `/auth/v1/authorize` Supabase côté client.
   *   - Le `redirect_to` pointe sur `${window.location.origin}/oauth-callback.html`,
   *     ce qui permet d'avoir automatiquement la bonne URL en dev (localhost:5174)
   *     comme en prod, sans dépendre d'une variable serveur (APP_URL).
   *   - Le fichier statique `oauth-callback.html` redirige ensuite vers la route
   *     Vue `/auth/callback` en passant les tokens en query string.
   *
   * Prérequis Supabase (à faire une fois par admin) :
   *   - Ajouter `http://localhost:5174/oauth-callback.html` (dev) et l'équivalent
   *     prod aux Redirect URLs dans Authentication → URL Configuration.
   */
  const loginWithGoogle = async () => {
    try {
      isLoading.value = true
      error.value = null

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      if (!supabaseUrl) throw new Error('VITE_SUPABASE_URL manquant')

      const redirectTo = `${window.location.origin}/oauth-callback.html`
      const scope = [
        'openid',
        'email',
        'profile',
        // calendar.readonly est requis pour lister les agendas (calendarList) ;
        // calendar.events pour créer/modifier des évènements.
        'https://www.googleapis.com/auth/calendar.readonly',
        'https://www.googleapis.com/auth/calendar.events',
        'https://www.googleapis.com/auth/contacts.readonly',
      ].join(' ')

      const url = new URL(`${String(supabaseUrl).replace(/\/+$/, '')}/auth/v1/authorize`)
      url.searchParams.set('provider', 'google')
      url.searchParams.set('redirect_to', redirectTo)
      url.searchParams.set('access_type', 'offline')
      url.searchParams.set('prompt', 'consent')
      // ⚠️ GoTrue attend `scopes` (pluriel) ; `scope` (singulier) est ignoré,
      // ce qui donnait un token Google sans accès Agenda → 403 calendarList.
      url.searchParams.set('scopes', scope)
      url.searchParams.set('include_granted_scopes', 'true')

      window.location.href = url.toString()
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }
  
  /**
   * Gère le callback OAuth
   */
  const handleOAuthCallback = async () => {
    try {
      isLoading.value = true
      
      const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession()
      
      if (sessionError) throw sessionError
      
      if (currentSession) {
        session.value = currentSession
        await fetchUserProfile()
        isAuthenticated.value = true
        return { success: true }
      }
      
      return { success: false, error: 'Aucune session trouvée' }
    } catch (err) {
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      isLoading.value = false
    }
  }
  
  /**
   * Déconnexion
   */
  const logout = async () => {
    try {
      isLoading.value = true
      
      await apiFetch('/api/public/auth/logout', {
        method: 'POST',
      })
      
      clearAuth()
      return { success: true }
    } catch (err) {
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      isLoading.value = false
    }
  }
  
  /**
   * Demande de réinitialisation de mot de passe
   */
  const forgotPassword = async (email) => {
    try {
      isLoading.value = true
      error.value = null
      
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })
      
      if (resetError) throw resetError
      
      return { success: true }
    } catch (err) {
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      isLoading.value = false
    }
  }
  
  /**
   * Met à jour le mot de passe
   */
  const updatePassword = async (newPassword) => {
    try {
      isLoading.value = true
      error.value = null
      
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      })
      
      if (updateError) throw updateError
      
      return { success: true }
    } catch (err) {
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      isLoading.value = false
    }
  }
  
  /**
   * Nettoie l'état d'authentification (Supabase + Google).
   * Appelé sur logout pour ne pas laisser de tokens orphelins en local.
   */
  const clearAuth = () => {
    user.value = null
    session.value = null
    isAuthenticated.value = false
    error.value = null
    try {
      localStorage.removeItem('g_at')
      localStorage.removeItem('g_at_exp')
      localStorage.removeItem('g_rt')
    } catch {
      /* localStorage indisponible */
    }
  }

  // ────────────────────────────────────────────────────────────────
  // Google provider token : refresh & persistance multi-jours
  // ────────────────────────────────────────────────────────────────

  /**
   * Lit les tokens Google depuis le localStorage.
   * - `g_at`     : access token (durée Google standard ≈ 1h)
   * - `g_rt`     : refresh token (longue durée, jusqu'à révocation)
   * - `g_at_exp` : timestamp ms d'expiration calculé au moment de la
   *                  remise (provider_token_expires_in × 1000 + Date.now())
   */
  const readGoogleTokens = () => {
    try {
      return {
        accessToken: localStorage.getItem('g_at') || null,
        refreshToken: localStorage.getItem('g_rt') || null,
        expiresAt: Number(localStorage.getItem('g_at_exp') || 0) || 0,
      }
    } catch {
      return { accessToken: null, refreshToken: null, expiresAt: 0 }
    }
  }

  /**
   * Considère un token "à rafraîchir" 60 secondes avant son expiration
   * pour absorber les latences réseau.
   */
  const isGoogleTokenExpired = () => {
    const { expiresAt } = readGoogleTokens()
    if (!expiresAt) return true // pas d'horodatage = on traite comme expiré
    return Date.now() >= expiresAt - 60_000
  }

  /**
   * Rafraîchit l'access token Google via l'API serveur (qui détient le
   * client_secret). Met à jour `g_at` + `g_at_exp` en localStorage.
   * Retourne le nouvel access token ou null si échec.
   *
   * En cas de refresh token invalide (Google répond { needsReauth }), on
   * purge les 3 entrées localStorage. L'UI proposera alors un bouton
   * « Connecter Google » qui relance le flux OAuth complet.
   */
  const refreshGoogleToken = async () => {
    const { refreshToken } = readGoogleTokens()
    if (!refreshToken) return null

    try {
      const resp = await apiFetch('/api/public/auth/refresh-google-token', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      })
      const payload = await resp.json().catch(() => ({}))

      if (!resp.ok) {
        // 401 + needsReauth = refresh token mort, on nettoie
        if (payload?.needsReauth || resp.status === 401) {
          try {
            localStorage.removeItem('g_at')
            localStorage.removeItem('g_at_exp')
            localStorage.removeItem('g_rt')
          } catch {
            /* noop */
          }
        }
        return null
      }

      const accessToken = payload.access_token
      const expiresIn = Number(payload.expires_in || 3600)
      if (accessToken) {
        try {
          localStorage.setItem('g_at', accessToken)
          localStorage.setItem('g_at_exp', String(Date.now() + expiresIn * 1000))
        } catch {
          /* noop */
        }
        return accessToken
      }
      return null
    } catch (err) {
      console.warn('[auth] refreshGoogleToken failed', err)
      return null
    }
  }

  /**
   * Retourne un access token Google valide, ou null si impossible.
   * Si le token actuel est expiré et qu'on dispose d'un refresh token,
   * tente un refresh transparent avant de retourner.
   *
   * À utiliser avant tout appel à l'API Google Calendar.
   */
  const getValidGoogleToken = async () => {
    const tokens = readGoogleTokens()
    if (tokens.accessToken && !isGoogleTokenExpired()) {
      return tokens.accessToken
    }
    if (!tokens.refreshToken) return null
    return await refreshGoogleToken()
  }

  /**
   * Refresh proactif au démarrage de l'app : si on a un refresh token
   * mais que l'access est expiré ou absent, on rafraîchit avant que les
   * composants en aient besoin. Évite un cycle « call → 401 → refresh ».
   */
  const bootGoogleTokenIfNeeded = async () => {
    const { refreshToken } = readGoogleTokens()
    if (!refreshToken) return
    if (!isGoogleTokenExpired()) return
    await refreshGoogleToken()
  }
  
  return {
    // State
    user,
    session,
    isLoading,
    error,
    isAuthenticated,
    
    // Getters
    isAdmin,
    isNotary,
    userType,
    userInitials,
    userDisplayName,
    
    // Actions
    checkAuthStatus,
    fetchUserProfile,
    login,
    loginWithGoogle,
    handleOAuthCallback,
    logout,
    forgotPassword,
    updatePassword,
    clearAuth,

    // Google token persistence
    refreshGoogleToken,
    getValidGoogleToken,
    bootGoogleTokenIfNeeded,
    isGoogleTokenExpired,
  }
})
